import { all, put, race, take, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getToken, getLocale } from '@global/selectors'

import {
    INSPECTIONS_TABLE_CHANGE,
    INSPECTIONS_TABLE_CHANGE_REQUEST,
    INSPECTIONS_TABLE_LOADING_TRUE,
    INSPECTIONS_TABLE_LOADING_FALSE,
    INSPECTIONS_TABLE_DATA_RECEIVED,
} from './constants'

import {
    inspectionsTableDataReceived,
    inspectionsTableChangeRequest,
    inspectionsTableLoadingTrue,
    inspectionsTableLoadingFalse,
} from './actions'

import { BLOCK_DATA_LOADED } from '../../../../../../../components/Workspace/components/HocBlock/constants'
import { blockSaveCustomData } from '../../../../../../../components/Workspace/components/HocBlock/actions'
import config from '../../../../../../../../../config'

const INSPECTIONS_TABLE_CHANGE_REQUEST_SUCCESS = (id) => `${INSPECTIONS_TABLE_CHANGE_REQUEST(id)}_${SUCCESS}`
const INSPECTIONS_TABLE_CHANGE_REQUEST_FAILED = (id) => `${INSPECTIONS_TABLE_CHANGE_REQUEST(id)}_${FAILED}`

function* onDataLoaded(_action) {
    const blockKey = _.get(_action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.inspections.key) return null

    const module = _.get(_action, 'data.module', null)
    const id = _.get(_action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    yield put(
        blockSaveCustomData(module, id, blockKey, {
            isLoading: false,
            data: _.get(_action, 'data.data[1].inspections', []),
            pagination: _.get(_action, 'data.data[1].query.pagination', {}),
            sorted: _.get(_action, 'data.data[1].query.sort.sortedColumns', []),
            filter: _.get(_action, 'data.data[1].query.filter', []),
        })
    )

    return null
}

function* inspectionsTableSaga(action) {
    const module = _.get(action, 'data.module', null)
    const blockKey = _.get(action, 'data.blockKey', null)
    const id = _.get(action, 'data.id', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const queryParams = _.get(action, 'data.queryParams', null)

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level) return null

    yield put(inspectionsTableLoadingTrue(module, id, blockKey, { ...customData, isLoading: true }))

    const filters = _.reduce(
        queryParams?.filtered,
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
        ...filters,
    }

    yield put(inspectionsTableChangeRequest(module, blockKey, id, level, lang, token, qp))

    const response = yield race({
        success: take(INSPECTIONS_TABLE_CHANGE_REQUEST_SUCCESS(id)),
        failed: take(INSPECTIONS_TABLE_CHANGE_REQUEST_FAILED(id)),
    })

    if (!response.failed) {
        const data = _.map(_.get(response, 'success.payload.response.body.inspections'), (row) => ({ ...row }))
        const query = _.get(response, 'success.payload.response.body.query', [])
        const pagination = _.get(query, 'pagination', null)
        const sorted = _.reduce(
            _.get(query, 'sort.sortedColumns'),
            (acc, val) => [
                ...acc,
                {
                    id: _.get(val, 'columm'), // TYPO FROM BACKEND
                    desc: _.get(val, 'direction') === 'desc',
                },
            ],
            []
        )
        const filter = _.get(query, 'filter', {})

        const newCustomData = {
            ...customData,
            data,
            pagination,
            sorted,
            filter,
            isLoading: false,
        }

        yield put(inspectionsTableDataReceived(module, id, blockKey, newCustomData))
    } else {
        yield put(inspectionsTableLoadingFalse(module, id, blockKey, { ...customData, isLoading: false }))
    }

    return null
}

function* inspectionsChangeCustomData(action) {
    const blockKey = _.get(action, 'data.blockKey', null)
    if (blockKey !== config.modules.report.blocks.inspections.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = _.get(action, 'data.customData', null)

    yield put(blockSaveCustomData(module, id, blockKey, customData))

    return null
}

export default function* () {
    yield all([
        takeEvery(BLOCK_DATA_LOADED, onDataLoaded),
        takeEvery(INSPECTIONS_TABLE_LOADING_TRUE, inspectionsChangeCustomData),
        takeEvery(INSPECTIONS_TABLE_LOADING_FALSE, inspectionsChangeCustomData),
        takeEvery(INSPECTIONS_TABLE_DATA_RECEIVED, inspectionsChangeCustomData),
        takeLatest(INSPECTIONS_TABLE_CHANGE, inspectionsTableSaga),
    ])
}
